import type { KeyboardEvent, ReactNode, RefObject } from "react";
import { CircleDot, CircleDollarSign, ClipboardList, LayoutDashboard, Megaphone, Package, ReceiptText, Settings, ShoppingCart, Users, UsersRound, WalletCards, Warehouse, X } from "lucide-react";
import type { LucideIcon } from "lucide-react";

const TAB_ICON_BY_LABEL = new Map<string, LucideIcon>([
  ["Visão geral", LayoutDashboard],
  ["Financeiro", CircleDollarSign],
  ["Mensalidades", WalletCards],
  ["Despesas", ReceiptText],
  ["Caixa", CircleDollarSign],
  ["Balcão", ShoppingCart],
  ["Agenda", ClipboardList],
  ["Aulas", ClipboardList],
  ["Modalidades", CircleDot],
  ["Tarefário", ClipboardList],
  ["Armazém", Warehouse],
  ["Alunos", Users],
  ["Equipe", UsersRound],
  ["Wellhub / TotalPass", Package],
  ["Configurações", Settings],
  ["Patch notes", Megaphone],
]);

const NAV_GROUPS: ReadonlyArray<readonly string[]> = [
  ["\u0056is\u00e3o geral", "Financeiro", "Mensalidades", "Despesas", "Caixa", "Balc\u00e3o"],
  ["Agenda", "Aulas", "Modalidades", "Planos"],
  ["Taref\u00e1rio", "Armaz\u00e9m", "Alunos", "Wellhub / TotalPass"],
  ["Equipe", "Configura\u00e7\u00f5es", "Patch notes"],
];

interface SidebarNavigationProps {
  readonly isMobile: boolean;
  readonly isDrawerOpen: boolean;
  readonly ctName: string;
  readonly tabs: readonly string[];
  readonly tab: string;
  readonly onTabSelection: (destination: string) => void;
  readonly onClose: () => void;
  readonly drawerCloseRef: RefObject<HTMLButtonElement | null>;
  readonly accountControls: ReactNode;
}

export function SidebarNavigation({
  isMobile,
  isDrawerOpen,
  ctName,
  tabs,
  tab,
  onTabSelection,
  onClose,
  drawerCloseRef,
  accountControls,
}: SidebarNavigationProps) {
  const handleDrawerKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (!isMobile || !isDrawerOpen || event.key !== "Tab") {
      return;
    }

    const focusableElements = Array.from(
      event.currentTarget.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    );
    const firstFocusableElement = focusableElements[0];
    const lastFocusableElement = focusableElements[focusableElements.length - 1];

    if (!firstFocusableElement || !lastFocusableElement) {
      return;
    }

    if (event.shiftKey && document.activeElement === firstFocusableElement) {
      event.preventDefault();
      lastFocusableElement.focus();
      return;
    }

    if (!event.shiftKey && document.activeElement === lastFocusableElement) {
      event.preventDefault();
      firstFocusableElement.focus();
    }
  };

  return (
    <>
      {isMobile && (
        <button
          className="app-shell__backdrop"
          type="button"
          aria-label="Fechar navegação"
          title="Fechar navegação"
          aria-hidden={!isDrawerOpen ? true : undefined}
          inert={!isDrawerOpen ? true : undefined}
          onClick={onClose}
        />
      )}
      <aside
        id="app-shell-navigation"
        className="app-shell__sidebar"
        aria-label="Barra lateral"
        aria-hidden={isMobile && !isDrawerOpen ? true : undefined}
        inert={isMobile && !isDrawerOpen ? true : undefined}
      >
          <div
            className="app-shell__sidebar-dialog"
            role={isMobile ? "dialog" : undefined}
            aria-modal={isMobile && isDrawerOpen ? true : undefined}
            aria-label={isMobile ? "Navegação móvel" : undefined}
            onKeyDown={handleDrawerKeyDown}
          >
            {isMobile && (
              <div className="app-shell__sidebar-identity">
                <span className="app-shell__sidebar-context">{ctName}</span>
                <button
                  ref={drawerCloseRef}
                  className="app-shell__drawer-close"
                  type="button"
                  aria-label="Fechar navegação"
                  title="Fechar navegação"
                  onClick={onClose}
                >
                  <X aria-hidden="true" size={20} />
                </button>
              </div>
            )}
          <nav className="app-shell__navigation" aria-label="Navegação principal">
            {NAV_GROUPS.map((group, groupIndex) => {
              const destinations = group.filter(destination => tabs.includes(destination));
              if (destinations.length === 0) return null;
              return <div className="app-shell__navigation-group" key={`group-${groupIndex}`}>
                {destinations.map(destination => {
                  const Icon = TAB_ICON_BY_LABEL.get(destination) ?? CircleDot;
                  const isCurrentDestination = tab === destination;
                  return <button key={destination} className="app-shell__navigation-item" type="button" aria-label={destination} aria-current={isCurrentDestination ? "page" : undefined} data-tooltip={destination} title={destination} onClick={() => onTabSelection(destination)}>
                    <Icon aria-hidden="true" size={18} strokeWidth={1.8} /><span className="app-shell__navigation-label">{destination}</span>
                  </button>;
                })}
              </div>;
            })}
          </nav>
          {isMobile && (
            <div className="app-shell__drawer-account-actions">
              {accountControls}
            </div>
          )}
        </div>
      </aside>
    </>
  );
}
